import React from 'react';
import { Form } from 'react-final-form';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { LanguageSelector } from '@unite-us/app-components';
import '@unite-us/ui/dist/main.css';
import '@unite-us/client-utils/dist/main.css';
import SurveyJSForm from '../../utils/index';
import PersonalForm from './PersonalForm';
import MilitaryForm from './MilitaryForm';
import Consent from './Signature/Consent';
import AssistanceRequestForm from './AssistanceRequestForm';
import LANGUAGES from '../../utils/languageEnums';

function HRSFAssistanceRequestForm({
  onSubmit,
  unsupportedLanguage,
  showLanguageSelector,
  handleLocaleChange,
  currentLanguage,
  assistanceReqConfigs,
  mappedLanguages,
  customForm,
  handleSetSurvey,
  serviceTypes,
  setBase64SignatureImage,
  failedSubmission,
  signature_image,
  goToFirstError,
  allowedServiceTypeIds,
  serviceTypeIdsToRemove,
}) {
  return (
    <div className="m-10">
      <Form
        onSubmit={onSubmit}
        initialValues={{}}
        render={({ handleSubmit, form, submitting }) => (
          <form onSubmit={handleSubmit}>
            {unsupportedLanguage === 'true' ? (
              <div className="mt-2 mb-4 font-bold text-red-700">
                It appears you have followed a direct link for a language that
                we do not currently support. The page here is displayed in
                English. Please use the language selector to translate it to the
                supported language of your choice.
              </div>
            ) : null}
            {showLanguageSelector && (
              <div className="mb-5">
                <LanguageSelector
                  onChange={handleLocaleChange}
                  field={{ value: currentLanguage }}
                  options={LANGUAGES.supportedList}
                />
              </div>
            )}
            <PersonalForm
              form={form}
              assistanceReqConfigs={assistanceReqConfigs}
              languagesOptions={mappedLanguages}
              isHRSF={true}
            />
            <MilitaryForm
              form={form}
              assistanceReqConfigs={assistanceReqConfigs}
            />
            <h3 className="mb-2 services ui-h3-heavy">
              <FormattedMessage
                id="9fnevE"
                description="services"
                defaultMessage="Services"
              />
            </h3>
            {serviceTypes && (
              <AssistanceRequestForm
                serviceTypes={serviceTypes}
                language={currentLanguage}
                allowedServiceTypeIds={allowedServiceTypeIds}
                serviceTypeIdsToRemove={serviceTypeIdsToRemove}
              />
            )}
            {customForm && (
              <SurveyJSForm
                id="custom-form"
                form={customForm.attributes}
                formRef={handleSetSurvey}
                expanded
              />
            )}
            <div className="form-group">
              <Consent
                handleSignatureEnd={setBase64SignatureImage}
                failedSubmission={failedSubmission}
                language={currentLanguage}
              />
              {!signature_image ? (
                <div
                  className="mt-2 mb-5 text-red-700"
                  style={{ marginTop: '-40px' }}
                >
                  <FormattedMessage
                    id="htKh5B"
                    description="signaturePrompt"
                    defaultMessage="Signature Required"
                  />
                </div>
              ) : (
                ''
              )}
            </div>
            <div class="sv_qstn pb-2">
              <FormattedMessage
                id="NP5ZPj"
                description="hrsfFooter1"
                defaultMessage="If you have questions about HRSF, need help filling out the form, or wish to file a grievance, please call CareOregon Customer Service at 503-416-4100 or toll-free 800-224-4840, TTY 711."
              />
              <br/>
              <br/>
              <FormattedMessage
                id="7VKO4V"
                description="hrsfFooter2"
                defaultMessage="You can get this in other languages, large print, braille or a format you prefer. You can also ask for an interpreter. This help is free. Call toll-free 800-224-4840 or TTY 711. We accept relay calls."
              />
            </div>
            <div className="flex">
              <div>
                {!submitting ? (
                  <button
                    className="px-8 py-2 mb-4 mr-8 font-bold text-white bg-blue-900"
                    type="submit"
                    onClick={() => goToFirstError()}
                  >
                    <FormattedMessage
                      id="AmnqMW"
                      description="submit"
                      defaultMessage="SUBMIT"
                    />
                  </button>
                ) : (
                  <FormattedMessage
                    id="KFYDxP"
                    description="submitting"
                    defaultMessage="Submitting..."
                  >
                    {(txt) => <p>{txt}</p>}
                  </FormattedMessage>
                )}
              </div>
              <div>
                {failedSubmission ? (
                  <div className="mt-2 font-bold text-red-700">
                    <FormattedMessage
                      id="9QH2Up"
                      description="failedSubmission"
                      defaultMessage="The form failed to submit. Please submit again."
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

HRSFAssistanceRequestForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  unsupportedLanguage: PropTypes.oneOf(['true', 'false']),
  showLanguageSelector: PropTypes.bool.isRequired,
  handleLocaleChange: PropTypes.func.isRequired,
  currentLanguage: PropTypes.string.isRequired,
  assistanceReqConfigs: PropTypes.object.isRequired,
  mappedLanguages: PropTypes.array.isRequired,
  customForm: PropTypes.object,
  handleSetSurvey: PropTypes.func.isRequired,
  serviceTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  setBase64SignatureImage: PropTypes.func.isRequired,
  failedSubmission: PropTypes.bool.isRequired,
  signature_image: PropTypes.string,
  goToFirstError: PropTypes.func.isRequired,
  allowedServiceTypeIds: PropTypes.arrayOf(PropTypes.string),
  serviceTypeIdsToRemove: PropTypes.arrayOf(PropTypes.string),
};

HRSFAssistanceRequestForm.defaultProps = {
  unsupportedLanguage: 'false',
  customForm: undefined,
  signature_image: null,
  allowedServiceTypeIds: null,
  serviceTypeIdsToRemove: null,
};

export default HRSFAssistanceRequestForm;
